import cx from "classnames";
import { faArrowRight, faRepeatAlt } from "@fortawesome/pro-regular-svg-icons";

import Icon from "common/components/Icon";
import LinkButton from "common/components/LinkButton";
import { toLocaleDate } from "common/utils/dates";

import { authoredLinkProps } from "client/routes/links";
import { ProductCardBadgePrice } from "client/products/types";

import { StorefrontCollectionRebate } from "../types";

import styles from "../styles/CollectionRebateCardList.module.less";

type CollectionRebateCardListProps = {
  rebates: StorefrontCollectionRebate[];
};

type CollectionRebateCardProps = {
  rebate: StorefrontCollectionRebate;
  index: number;
};

/** Colours defined in the stylesheet; positions past the last one start over. */
const CARD_TONE_COUNT = 5;

const CollectionRebateCard = ({ rebate, index }: CollectionRebateCardProps) => {
  const linkProps = authoredLinkProps(rebate.url);
  const className = cx(styles.card, styles[`tone${index % CARD_TONE_COUNT}`]);
  const card = (
    <>
      <div className={styles.body}>
        <div className={styles.meta}>
          {!!rebate.chip && (
            <span className={styles.chip}>
              {rebate.applies_to === ProductCardBadgePrice.Subscription && (
                <Icon icon={faRepeatAlt} title="Autoship" />
              )}
              {rebate.chip}
            </span>
          )}
          {!!rebate.brand && <span className={styles.brand}>{rebate.brand}</span>}
        </div>
        <div className={styles.title}>{rebate.title}</div>
        {rebate.promo_code ? (
          <div className={styles.code}>
            <span className={styles.codeLabel}>Use code</span>
            <span className={styles.codeValue}>{rebate.promo_code}</span>
          </div>
        ) : (
          <div className={styles.applied}>Applied automatically at checkout</div>
        )}
        {!!rebate.end_date && (
          <div className={styles.validity}>
            Valid through {toLocaleDate(rebate.end_date, { month: "short", day: "numeric" })}
          </div>
        )}
      </div>
      <Icon className={styles.arrow} icon={faArrowRight} />
    </>
  );
  if (!linkProps) {
    return <div className={className}>{card}</div>;
  }
  return (
    <LinkButton {...linkProps} className={className}>
      {card}
    </LinkButton>
  );
};

/** The page's highlighted rebates as a row of cards under the hero; nothing when there are none. */
const CollectionRebateCardList = ({ rebates }: CollectionRebateCardListProps) =>
  rebates.length ? (
    <div className={styles.list}>
      {rebates.map((rebate, index) => (
        <CollectionRebateCard key={rebate.id} rebate={rebate} index={index} />
      ))}
    </div>
  ) : null;

export default CollectionRebateCardList;
